import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { AlertTriangle, X, ArrowRight } from "lucide-react";

// How soon qualifies as "due" for the soft banner. 90d gives a bit of lead
// time ahead of the 60d hard admin alert so Rohan sees it earlier.
const WINDOW_DAYS = 90;
const dismissKey = (clientId) => `reliserve:renewal-banner-dismissed:${clientId}`;

function daysUntil(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return Math.ceil((d - new Date()) / (1000 * 60 * 60 * 24));
}

function fmtDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" });
}

/**
 * Soft "Renewal due" banner shown at the top of the Client Workspace when
 * any policy expires within WINDOW_DAYS. Clicking "Open Insurance" jumps
 * to the Insurance tab via a tab hash change event the parent listens to.
 * Dismiss-per-session (per client) so it doesn't pester after acknowledgement.
 */
export default function InsuranceRenewalBanner({ clientId, hidden, onJumpToInsurance }) {
  const [policies, setPolicies] = useState([]);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (hidden) return;
    try {
      if (sessionStorage.getItem(dismissKey(clientId)) === "1") setDismissed(true);
    } catch { /* ignore */ }
    (async () => {
      try {
        const { data } = await api.get(`/clients/${clientId}/insurance-policies`);
        setPolicies(data.policies || []);
      } catch { /* silent — banner just stays hidden */ }
    })();
  }, [clientId, hidden]);

  // Pick policies expiring within the window and sort soonest-first. Already-
  // expired (<0 days) and policies currently in `Renewed` status are excluded —
  // the latter means Admin has already rolled over to a new cycle.
  const due = useMemo(() => {
    return (policies || [])
      .map((p) => ({ p, d: daysUntil(p.expiry_date) }))
      .filter(({ p, d }) =>
        d !== null && d >= 0 && d <= WINDOW_DAYS && p.renewal_status !== "Renewed",
      )
      .sort((a, b) => a.d - b.d);
  }, [policies]);

  if (hidden || dismissed || due.length === 0) return null;

  const soonest = due[0];
  const extra = due.length - 1;
  const dismiss = () => {
    try { sessionStorage.setItem(dismissKey(clientId), "1"); } catch { /* ignore */ }
    setDismissed(true);
  };

  // Pick a tone based on urgency so the signal degrades gracefully: amber for
  // approaching, rose for last-leg (≤14 days).
  const urgent = soonest.d <= 14;
  const tone = urgent
    ? "border-rose-200 bg-rose-50 text-rose-900"
    : "border-amber-200 bg-amber-50 text-amber-900";
  const iconTone = urgent ? "text-rose-600" : "text-amber-600";

  return (
    <div
      className={`flex flex-wrap items-start gap-3 rounded-md border p-3 text-sm ${tone}`}
      role="status"
      data-testid="renewal-due-banner"
    >
      <AlertTriangle className={`mt-0.5 h-4 w-4 shrink-0 ${iconTone}`} />
      <div className="min-w-0 flex-1">
        <div className="font-semibold" data-testid="renewal-due-banner-title">
          Renewal due in {soonest.d} day{soonest.d === 1 ? "" : "s"}
          {extra > 0 && (
            <span className="ml-1 font-normal text-foreground/70">
              (+ {extra} more policy expiring soon)
            </span>
          )}
        </div>
        <div className="mt-0.5 text-xs text-foreground/80">
          <b>{soonest.p.insurer || "—"}</b> · {soonest.p.policy_type} · expires {fmtDate(soonest.p.expiry_date)}
          {soonest.p.renewal_status && soonest.p.renewal_status !== "None" && (
            <> · <span className="italic">{soonest.p.renewal_status}</span></>
          )}
        </div>
      </div>
      <div className="flex items-center gap-1">
        <Button
          size="sm"
          variant="outline"
          className="gap-1"
          onClick={onJumpToInsurance}
          data-testid="renewal-due-banner-open"
        >
          Open Insurance <ArrowRight className="h-3 w-3" />
        </Button>
        <Button
          size="sm"
          variant="ghost"
          onClick={dismiss}
          className="h-8 w-8 p-0"
          aria-label="Dismiss"
          data-testid="renewal-due-banner-dismiss"
        >
          <X className="h-3.5 w-3.5" />
        </Button>
      </div>
    </div>
  );
}
